import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { DatePicker } from '../../components/DatePicker';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import {
  getPlatformIcon,
  renderPlatformIcons,
  useSocialMediaOptions,
} from '../../utils/socialMediaOptions';
import PostDetailModal from '../../components/SocialMedia/PostDetailModal';
import { resolveBadgeStyle } from '../../utils/badgeColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarView = () => {
  const { token, user, isReadOnly, canModify } = useAuth();
  const { can } = usePermissions();
  const { PLATFORMS, PLATFORM_ICONS, ACCOUNTS, COLLEGES_FOR_SELECT: COLLEGES, CONTENT_TYPES, STATUSES, ACCOUNT_COLORS, COLLEGE_COLORS, CONTENT_TYPE_COLORS } = useSocialMediaOptions();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dateView, setDateView] = useState('scheduled'); // 'scheduled', 'posted', or 'received'
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // State for viewing all posts of a day
  const [isDayPostsDialogOpen, setIsDayPostsDialogOpen] = useState(false);
  const [selectedDayPosts, setSelectedDayPosts] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  // State for editing
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    content_name: '',
    platforms: [],
    account: '',
    college: '',
    content_type: '',
    host_by: '',
    approved_for_posting: false,
    content_received_date: '',
    scheduled_date: '',
    posted_date: '',
    status: 'Pending',
    notes: ''
  });

  useEffect(() => {
    loadCalendarPosts();
    loadUsers();
  }, [currentDate]);

  const loadCalendarPosts = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await axios.get(
        `${API}/social-media/calendar?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(response.data);
      window.dispatchEvent(new Event('social-media-posts-changed'));
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  // Helper function to get user name by ID
  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  // Handle edit from detail modal
  const handleEditFromDetail = () => {
    if (selectedPost) {
      setEditingPost(selectedPost);
      setFormData({
        content_name: selectedPost.content_name || '',
        platforms: selectedPost.platforms || [],
        account: selectedPost.account || '',
        college: selectedPost.college || '',
        content_type: selectedPost.content_type || '',
        host_by: selectedPost.host_by || '',
        approved_for_posting: selectedPost.approved_for_posting || false,
        content_received_date: selectedPost.content_received_date ? format(new Date(selectedPost.content_received_date), 'yyyy-MM-dd') : '',
        scheduled_date: selectedPost.scheduled_date ? format(new Date(selectedPost.scheduled_date), 'yyyy-MM-dd') : '',
        posted_date: selectedPost.posted_date ? format(new Date(selectedPost.posted_date), 'yyyy-MM-dd') : '',
        status: selectedPost.status || 'Pending',
        notes: selectedPost.notes || ''
      });
      setIsDetailOpen(false);
      setIsEditDialogOpen(true);
    }
  };

  // Handle edit post submission
  const handleEditPost = async (e) => {
    e.preventDefault();
    if (!editingPost) return;
    
    try {
      await axios.put(
        `${API}/social-media/posts/${editingPost.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post updated successfully');
      setIsEditDialogOpen(false);
      setEditingPost(null);
      loadCalendarPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = monthStart.getDay();
  const emptyDays = Array(firstDayOfWeek).fill(null);

  const getPostsForDay = (day) => {
    return posts.filter(post => {
      let dateToCheck;
      if (dateView === 'scheduled') {
        dateToCheck = post.scheduled_date;
      } else if (dateView === 'posted') {
        dateToCheck = post.posted_date;
      } else if (dateView === 'received') {
        dateToCheck = post.content_received_date;
      }
      
      if (!dateToCheck) return false;
      const postDate = new Date(dateToCheck);
      return isSameDay(postDate, day);
    });
  };

  const handleDayClick = (day) => {
    setSelectedDate(day);
    setFormData({
      ...formData,
      scheduled_date: format(day, 'yyyy-MM-dd'),
      content_received_date: format(new Date(), 'yyyy-MM-dd')
    });
    setIsCreateDialogOpen(true);
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    if (!formData.content_name || formData.platforms.length === 0) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API}/social-media/posts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created successfully!');
      setIsCreateDialogOpen(false);
      resetForm();
      loadCalendarPosts();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const resetForm = () => {
    setFormData({
      content_name: '',
      platforms: [],
      account: '',
      college: '',
      content_type: '',
      host_by: '',
      approved_for_posting: false,
      content_received_date: '',
      scheduled_date: '',
      posted_date: '',
      status: 'Pending',
      notes: ''
    });
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-lime-500',
      'Published': 'bg-lime-500',
      'Scheduled': 'bg-yellow-500',
      'In Shoot': 'bg-blue-500',
      'In Editing': 'bg-purple-500',
      'In Corrections': 'bg-orange-500',
      'Out Delivered': 'bg-teal-500',
      'Pending': 'bg-slate-400',
      'Not Approved': 'bg-red-500'
    };
    return colors[status] || colors['Pending'];
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsDetailOpen(true);
  };

  // Handle clicking "+X more" to show all posts for that day
  const handleViewMorePosts = (day, dayPosts) => {
    setSelectedDayDate(day);
    setSelectedDayPosts(dayPosts);
    setIsDayPostsDialogOpen(true);
  };

  // Handle clicking a post from the day posts dialog
  const handlePostClickFromDayDialog = (post) => {
    setIsDayPostsDialogOpen(false);
    setSelectedPost(post);
    setIsDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-calendar">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Content Calendar</h1>
          <p className="page-subtitle">Visual posting schedule</p>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
            <ChevronLeft size={20} />
          </Button>
          <h2 className="text-xl font-bold text-slate-800 min-w-[200px] text-center">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <Button variant="outline" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
            <ChevronRight size={20} />
          </Button>
          <Button variant="outline" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
        </div>
      </div>

      {/* Date View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-1">Calendar View</h3>
            <p className="text-xs text-slate-500">Toggle between scheduled, posted, and content received dates</p>
          </div>
          <div className="flex gap-2">
          <Button
              variant={dateView === 'received' ? 'default' : 'outline'}
              onClick={() => setDateView('received')}
              className={dateView === 'received' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : ''}
              data-testid="toggle-received"
            >
              📥 Content Received
            </Button>
            <Button
              variant={dateView === 'scheduled' ? 'default' : 'outline'}
              onClick={() => setDateView('scheduled')}
              className={dateView === 'scheduled' ? 'btn-cta' : ''}
              data-testid="toggle-scheduled"
            >
              📅 Scheduled Date
            </Button>
            <Button
              variant={dateView === 'posted' ? 'default' : 'outline'}
              onClick={() => setDateView('posted')}
              className={dateView === 'posted' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : ''}
              data-testid="toggle-posted"
            >
              ✅ Posted Date
            </Button>
            
          </div>
        </div>
      </div>

      {/* RBAC Feedback Banner */}
      {user && !can('social_media.approve') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="rbac-info-banner">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-sm text-blue-700">
            You are viewing content based on your assigned brands. Contact your manager for additional access.
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Status Legend</h3>
        <div className="flex flex-wrap gap-4">
          {[
            { status: 'Published', color: 'bg-lime-500' },
            { status: 'Out Delivered', color: 'bg-teal-500' },
            { status: 'Scheduled', color: 'bg-yellow-500' },
            { status: 'In Shoot', color: 'bg-blue-500' },
            { status: 'In Editing', color: 'bg-purple-500' },
            { status: 'In Corrections', color: 'bg-orange-500' },
            { status: 'Pending', color: 'bg-slate-400' },
            { status: 'Not Approved', color: 'bg-red-500' }
          ].map(({ status, color }) => (
            <div key={status} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${color}`}></div>
              <span className="text-xs text-slate-600">{status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-4 text-center font-semibold text-slate-600 text-sm">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {/* Empty days before month starts */}
          {emptyDays.map((_, index) => (
            <div key={`empty-${index}`} className="min-h-[120px] border border-slate-100 bg-slate-50"></div>
          ))}

          {/* Actual days */}
          {daysInMonth.map(day => {
            const dayPosts = getPostsForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toString()}
                className={`min-h-[120px] border border-slate-100 p-2 hover:bg-slate-50 transition-colors cursor-pointer ${
                  isToday ? 'bg-cyan-50 border-cyan-300' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-sm font-semibold ${
                    isToday ? 'text-cyan-600' : 'text-slate-700'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <button
                    onClick={() => handleDayClick(day)}
                    className="p-1 hover:bg-cyan-100 rounded transition-colors"
                    title="Create new post"
                    data-testid={`add-post-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <Plus size={16} className="text-cyan-600" />
                  </button>
                </div>
                
                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map(post => (
                    <button
                      key={post.id}
                      onClick={() => handlePostClick(post)}
                      className={`w-full text-left p-2 rounded text-xs text-white ${getStatusColor(post.status)} hover:opacity-80 transition-opacity`}
                    >
                      <div className="flex items-start gap-1">
                        <span className="flex items-center gap-0.5 shrink-0">{renderPlatformIcons(post.platforms, 12, PLATFORM_ICONS)}</span>
                        <span className="truncate flex-1">{post.content_name}</span>
                      </div>
                    </button>
                  ))}
                  {dayPosts.length > 3 && (
                    <button
                      onClick={() => handleViewMorePosts(day, dayPosts)}
                      className="w-full text-xs text-cyan-600 hover:text-cyan-800 text-center py-1 hover:bg-cyan-50 rounded transition-colors font-medium"
                      data-testid={`view-more-${format(day, 'yyyy-MM-dd')}`}
                    >
                      +{dayPosts.length - 3} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Post Detail Modal - Using standardized component */}
      <PostDetailModal
        isOpen={isDetailOpen}
        onClose={setIsDetailOpen}
        post={selectedPost}
        users={users}
        canModify={canModify}
        onEdit={handleEditFromDetail}
        getUserName={getUserName}
      />

      {/* Day Posts Dialog - Shows all posts for a selected day */}
      <Dialog open={isDayPostsDialogOpen} onOpenChange={setIsDayPostsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon size={20} className="text-cyan-600" />
              Posts for {selectedDayDate && format(selectedDayDate, 'MMMM dd, yyyy')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-2">
            {selectedDayPosts.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No posts for this day</p>
            ) : (
              selectedDayPosts.map((post, index) => (
                <button
                  key={post.id}
                  onClick={() => handlePostClickFromDayDialog(post)}
                  className={`w-full text-left p-3 rounded-lg border transition-all hover:shadow-md ${
                    getStatusColor(post.status).replace('bg-', 'border-l-4 border-l-').replace('500', '500 bg-white border-slate-200')
                  }`}
                  data-testid={`day-post-${index}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="flex items-center gap-1">{renderPlatformIcons(post.platforms, 14, PLATFORM_ICONS)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor(post.status)}`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="font-medium text-slate-900 truncate">{post.content_name}</p>
                      {post.account && (() => {
                        const accBadge = resolveBadgeStyle(post.account, ACCOUNT_COLORS);
                        const collegeLabel = post.college;
                        const collegeBadge = collegeLabel ? resolveBadgeStyle(collegeLabel, COLLEGE_COLORS) : null;
                        return (
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${accBadge.className}`} style={accBadge.style}>
                              {post.account}
                            </span>
                            {collegeLabel && (
                              <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${collegeBadge.className}`} style={collegeBadge.style}>
                                {collegeLabel}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="text-xs text-slate-400">
                      {post.content_type ? (() => {
                        const ctBadge = resolveBadgeStyle(post.content_type, CONTENT_TYPE_COLORS);
                        return (
                          <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${ctBadge.className}`} style={ctBadge.style}>
                            {post.content_type}
                          </span>
                        );
                      })() : ''}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="pt-4 border-t border-slate-200 mt-2">
            <p className="text-sm text-slate-600 text-center">
              Showing <span className="font-semibold text-slate-900">{selectedDayPosts.length}</span> post{selectedDayPosts.length !== 1 ? 's' : ''} • Click to view details
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create New Post - {selectedDate && format(selectedDate, 'MMMM dd, yyyy')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePost} className="space-y-4">
            <div className="space-y-2">
              <Label>Content Name *</Label>
              <Input
                value={formData.content_name}
                onChange={(e) => setFormData({...formData, content_name: e.target.value})}
                placeholder="Enter content name"
                required
                data-testid="content-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Platforms *</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(platform => (
                  <div key={platform} className="flex items-center space-x-2">
                    <Checkbox
                      id={platform}
                      checked={formData.platforms.includes(platform)}
                      onCheckedChange={() => togglePlatform(platform)}
                    />
                    <label htmlFor={platform} className="text-sm cursor-pointer">
                      {platform}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account</Label>
                <Select value={formData.account} onValueChange={(value) => setFormData({...formData, account: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNTS.map(acc => (
                      <SelectItem key={acc} value={acc}>{acc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>College</Label>
                <Select value={formData.college} onValueChange={(value) => setFormData({...formData, college: value})}>
                  <SelectTrigger data-testid="calendar-college-select">
                    <SelectValue placeholder="Select college" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLEGES.map(col => (
                      <SelectItem key={col} value={col}>{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select value={formData.content_type} onValueChange={(value) => setFormData({...formData, content_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Host By</Label>
              <Input
                value={formData.host_by}
                onChange={(e) => setFormData({...formData, host_by: e.target.value})}
                placeholder="Enter host name"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <DatePicker
                  value={formData.scheduled_date}
                  onChange={(v) => setFormData({...formData, scheduled_date: v})}
                />
              </div>

              <div className="space-y-2">
                <Label>Posted Date</Label>
                <DatePicker
                  value={formData.posted_date}
                  onChange={(v) => setFormData({...formData, posted_date: v})}
                />
              </div>

              <div className="space-y-2">
                <Label>Content Received</Label>
                <DatePicker
                  value={formData.content_received_date}
                  onChange={(v) => setFormData({...formData, content_received_date: v})}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="approved"
                checked={formData.approved_for_posting}
                onCheckedChange={(checked) => setFormData({...formData, approved_for_posting: checked})}
              />
              <label htmlFor="approved" className="text-sm cursor-pointer">
                Approved for Posting
              </label>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Add any notes or comments..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-cta">
                <Plus size={16} className="mr-2" />
                Create Post
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPost} className="space-y-4">
            <div className="space-y-2">
              <Label>Content Name *</Label>
              <Input
                value={formData.content_name}
                onChange={(e) => setFormData({...formData, content_name: e.target.value})}
                placeholder="Enter content name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Platforms *</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(platform => (
                  <label key={platform} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={formData.platforms.includes(platform)}
                      onCheckedChange={(checked) => {
                        const newPlatforms = checked
                          ? [...formData.platforms, platform]
                          : formData.platforms.filter(p => p !== platform);
                        setFormData({...formData, platforms: newPlatforms});
                      }}
                    />
                    <span className="text-sm">{getPlatformIcon(platform, 16, PLATFORM_ICONS)} {platform}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account</Label>
                <Select value={formData.account} onValueChange={(value) => setFormData({...formData, account: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNTS.map(account => (
                      <SelectItem key={account} value={account}>{account}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>College</Label>
                <Select value={formData.college} onValueChange={(value) => setFormData({...formData, college: value})}>
                  <SelectTrigger data-testid="calendar-edit-college-select">
                    <SelectValue placeholder="Select college" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLEGES.map(college => (
                      <SelectItem key={college} value={college}>{college}</SelectItem>
                    ))}
                    {formData.college && !COLLEGES.includes(formData.college) && (
                      <SelectItem key={formData.college} value={formData.college}>
                        {formData.college} (archived)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select value={formData.content_type} onValueChange={(value) => setFormData({...formData, content_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Host By</Label>
              <Input
                value={formData.host_by}
                onChange={(e) => setFormData({...formData, host_by: e.target.value})}
                placeholder="Enter host name"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <DatePicker
                  value={formData.scheduled_date}
                  onChange={(v) => setFormData({...formData, scheduled_date: v})}
                />
              </div>

              <div className="space-y-2">
                <Label>Posted Date</Label>
                <DatePicker
                  value={formData.posted_date}
                  onChange={(v) => setFormData({...formData, posted_date: v})}
                />
              </div>

              <div className="space-y-2">
                <Label>Content Received</Label>
                <DatePicker
                  value={formData.content_received_date}
                  onChange={(v) => setFormData({...formData, content_received_date: v})}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-approved"
                checked={formData.approved_for_posting}
                onCheckedChange={(checked) => setFormData({...formData, approved_for_posting: checked})}
              />
              <label htmlFor="edit-approved" className="text-sm cursor-pointer">
                Approved for Posting
              </label>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Add any notes or comments..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingPost(null); }}>
                Cancel
              </Button>
              <Button type="submit" className="btn-cta">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
